import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import ReactDOMServer from 'react-dom/server';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Tooltip } from 'react-tooltip';
import classNames from 'classnames';
import useOutsideClick from 'utils/useOutsideClick';
import Icon from './Icon';
import ToolbarDropdownAutocompleteModes from './ToolbarDropdownAutocompleteModes';
import lizzyApi from 'lizzyApiAdapter';

const options = [
  {
    title: 'Autocomplete mode',
    icon: 'lizzy-autocomplete',
    key: 'autocomplete',
    modes: [
      {
        key: 'inline',
        icon: 'lizzy-autocomplete-inline',
        label: 'Inline Autocomplete'
      },
      {
        key: 'dropdown',
        icon: 'lizzy-autocomplete-dropdown',
        label: 'Dropdown Autocomplete'
      },
      {
        key: 'off',
        icon: 'lizzy-autocomplete-off',
        label: 'Autocomplete Off'
      },
    ],
  },
  {
    title: 'Contract Overview',
    icon: 'lizzy-contract-overview',
    key: 'contract-overview',
  },
];

const AUTOCOMPLETE_MODE_ICONS = {
  inline: 'lizzy-autocomplete-inline',
  dropdown: 'lizzy-autocomplete-dropdown',
  off: 'lizzy-autocomplete-off',
};

const WidgetSwitcher = ({ onWidgetClick }) => {
  /* State */
  const [dropdown, setDropdown] = useState('');
  const [active, setActive] = useState(false);

  /* Refs */
  const refDropdown = useOutsideClick(() => {
    setDropdown('');
    setActive(false);
  });

  /* Selectors */
  const mode = useSelector((state) => state.autocomplete.mode);

  /* Functions */
  const handleOptionClick = (option) => {
    lizzyApi.emit(`widgetClick:${option.key}`);

    if(option.modes) {
      setDropdown(!dropdown ? option.key : '');
    }

    setActive(false);
  };

  const handleWidgetClick =  useCallback(() => {
    lizzyApi.emit('widgetClick');
  }, []);

  const onModesOptionClick = () => {
    setDropdown('');
  };

  const mouseLeaveHandler = () => {
    if (!dropdown) {
      setActive(false);
    }
  };

  const mouseOverHandler = () => {
    setActive(true);
  };

  const getIcon = (option) => {
    if (option.key === 'autocomplete') {
      return AUTOCOMPLETE_MODE_ICONS[mode];
    }

    return option.icon;
  };

  const isDropdownOpen = (option) => option.modes && option.key === dropdown;

  const hintContent = ReactDOMServer.renderToStaticMarkup(
    <>
      Launch Lizzy <HintKey>⌘</HintKey> <HintKey>L</HintKey>
    </>
  );

  /* Render */
  return (
    <SwitcherWrapper
      data-exclude-outside-click-id='widget-button'
      onMouseLeave={mouseLeaveHandler}
      onMouseOver={mouseOverHandler}
      className={classNames({ active })}
    >
      <Tooltip id='switcher' />
      <LogoButton
        onClick={handleWidgetClick}
        data-tooltip-id='switcher'
        data-tooltip-html={hintContent}
        data-tooltip-delay-show='2000'
      >
        <Icon source='lizzy' name='lizzy-logo-btn' />
      </LogoButton>

      <Backdrop>
        {options.map((option) => (
          <Item key={option.key}>
            <Tooltip id='switcher-options' />
            <button
              onClick={() => handleOptionClick(option)}
              data-tooltip-id='switcher-options'
              data-tooltip-content={option.key !== dropdown ? option.title : ''}
              data-tooltip-delay-show='1000'
            >
              <Icon name={getIcon(option)} />
            </button>
            
            {isDropdownOpen(option) && (
              <ToolbarDropdownAutocompleteModes
                ref={refDropdown}
                options={option.modes}
                onOptionClick={onModesOptionClick}
              />
            )}
          </Item>
        ))}
      </Backdrop>
    </SwitcherWrapper>
  );
}

export default WidgetSwitcher;

WidgetSwitcher.propTypes = {
  onWidgetClick: PropTypes.func
};

WidgetSwitcher.defaultProps = {
  onWidgetClick: () => {},
};

const LogoButton = styled.button.attrs(() => ({ type: 'button' }))`
  all: unset;
  position: relative;
  z-index: 2;
  display: inline-block;
`;

const Item = styled.div`
  position: relative;
`;

const Backdrop = styled.div`
  position: absolute;
  background: var(--lizzy-white);
  top: -5px;
  right: -5px;
  display: flex;
  border-radius: 42px;
  padding: 8px 44px 4px 8px;
  filter: drop-shadow(0px 0px 58px rgba(17, 17, 17, 0.1));
  z-index: 1;
  opacity: 0;
  overflow: hidden;
  width: 0;
  font-family: 'Inter';
  transition: opacity 0.2s ease 0.3s, width 0.2s ease 0s;

  button {
    all: unset;
    width: 36px;
    height: 36px;
    border-radius: 36px;
    margin-right: 5px;

    &:hover {
      background-color: var(--lizzy-black-5);
    }
  }
`;

const SwitcherWrapper = styled.div`
  position: fixed;
  bottom: 50px;
  right: 50px;
  transition: transform 0.2s ease;
  cursor: pointer;
  z-index: 12;

  &.active ${Backdrop} {
    transition: opacity 0.2s ease 1s, width 0.2s ease 1s;
    width: 90px;
    opacity: 1;
    overflow: unset;
  }
`;

const HintKey = styled.span`
  display: inline-block;
  background-color: var(--lizzy-black-35);
  color: var(--lizzy-white);
  border-radius: 3px;
  width: 18px;
  height: 18px;
  text-align: center;
  font-weight: 600;

  &:first-child {
    margin-left: 10px;
  }
`